/**
 * Creates a `ResizeObserver` whose callback is deferred to the next
 * animation frame, coalescing the latest report for each observed element.
 *
 * Why: when a `ResizeObserver` callback synchronously writes layout state
 * — measure -> setState -> reflow -> the observer fires again — the browser
 * can fail to deliver all notifications in a single frame and emits the
 * benign "ResizeObserver loop completed with undelivered notifications"
 * (a.k.a. "loop limit exceeded") error on `window.onerror`. Deferring the
 * callback into `requestAnimationFrame` breaks the synchronous loop.
 *
 * The returned observer's `disconnect()` is overridden to also cancel any
 * pending frame, so existing cleanup calls (`observer.disconnect()`) keep
 * working and never fire a stale callback after unmount.
 */
export function createRafResizeObserver(
  callback: ResizeObserverCallback
): ResizeObserver {
  let rafId = 0
  const pendingEntries = new Map<Element, ResizeObserverEntry>()
  const ro = new ResizeObserver((entries, observer) => {
    for (const entry of entries) {
      pendingEntries.set(entry.target, entry)
    }
    if (rafId) {
      return
    }
    rafId = requestAnimationFrame(() => {
      rafId = 0
      const latestEntries = [...pendingEntries.values()]
      pendingEntries.clear()
      callback(latestEntries, observer)
    })
  })
  const origDisconnect = ro.disconnect.bind(ro)
  ro.disconnect = () => {
    if (rafId) {
      cancelAnimationFrame(rafId)
    }
    rafId = 0
    pendingEntries.clear()
    origDisconnect()
  }
  return ro
}
